import React, { useEffect, useState, useRef } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { Button, Form, Card, InputGroup, Alert } from 'react-bootstrap';
import { Formik } from 'formik';
import { FaUser, FaLock } from 'react-icons/fa';
import BeatLoader from 'react-spinners/BeatLoader';

import api from '~/Services/api';
import AuthStorage from '~/Utils/AuthStorage';
import param from '~/Utils/ParamParser';
import { LoginContainer } from './styles';
import { loginValidationSchema } from '~/Validations';

import Logo from '~/Components/Logo';

const Login = props => {
  const abortControllerRef = useRef(null);

  const [isLogout, setIsLogout] = useState(param.has(props, 'logout'));
  const [isExpired, setIsExpired] = useState(param.has(props, 'expired'));
  const [userLogged, setUserLogged] = useState(AuthStorage.isLogged());
  const [loginError, setLoginError] = useState(null);
  const [activityMessage, setActivityMessage] = useState(null);

  const history = useHistory();

  useEffect(() => {
    if (isLogout) {
      setTimeout(() => {
        setIsLogout(false);
      }, 3000);
    }
    if (isExpired) {
      setTimeout(() => {
        setIsExpired(false);
      }, 3000);
    }
  });

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  async function doLogin(values, actions) {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    setUserLogged(false);
    setLoginError(null);

    try {
      if (signal.aborted) return;
      setActivityMessage('Autenticando');

      const user = await api.login({ ...values }, undefined, {}, signal);
      if (signal.aborted) return;

      AuthStorage.setUser(user);
      setActivityMessage('Buscando configurações');

      const claims = await api.getClaims(signal);
      if (signal.aborted) return;

      AuthStorage.setCaracteristicas(claims.caracteristicas);
      setActivityMessage('Verificando caixa');

      const temChave = await api.verificarTemChaveNaCaixa(signal);
      if (signal.aborted) return;
      const { from } = props.location.state || { from: { pathname: '/' } };
      if (temChave) {
        history.push('/negocios/caixa-aprovacao');
      } else {
        history.push(from.pathname || '/');
      }
    } catch (error) {
      if (error.name !== 'AbortError' && !signal.aborted) {
        const errorMessage =
          error.error_description ||
          error.message ||
          'Usuário ou senha inválidos';
        setLoginError(errorMessage);
        actions.setSubmitting(false);
      }
    } finally {
      if (!signal.aborted) {
        setActivityMessage(null);
      }
    }
  }

  if (userLogged) {
    return <Redirect to="/" />;
  }

  return (
    <LoginContainer>
      <Card className="border-0">
        <Card.Body>
          <Logo size={50} center />
          {isLogout && (
            <div className="alert alert-info">Você desconectou!</div>
          )}
          {isExpired && (
            <div className="alert alert-info">Sua sessão expirou</div>
          )}

          <p className="text-secondary">
            Informe o seu usuário e senha para acessar
          </p>
          <Formik
            validationSchema={loginValidationSchema}
            onSubmit={doLogin}
            initialValues={{
              username: '',
              password: '',
            }}
          >
            {({
              handleSubmit,
              handleChange,
              values,
              touched,
              errors,
              isSubmitting,
            }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <InputGroup>
                  <InputGroup.Prepend>
                    <InputGroup.Text>
                      <FaUser />
                    </InputGroup.Text>
                  </InputGroup.Prepend>
                  <Form.Control
                    type="text"
                    placeholder="Usuário"
                    name="username"
                    autoComplete="username"
                    value={values.username}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    isInvalid={touched.username && errors.username}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.username}
                  </Form.Control.Feedback>
                </InputGroup>
                <InputGroup>
                  <InputGroup.Prepend>
                    <InputGroup.Text>
                      <FaLock />
                    </InputGroup.Text>
                  </InputGroup.Prepend>
                  <Form.Control
                    type="password"
                    placeholder="Senha"
                    name="password"
                    autoComplete="current-password"
                    value={values.password}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    isInvalid={touched.password && errors.password}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.password}
                  </Form.Control.Feedback>
                </InputGroup>
                <Button type="submit" variant="primary" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <div className="d-flex justify-content-center">
                      <span className="mr-2">{activityMessage}</span>
                      <BeatLoader size={8} color="#CDCDCD" />
                    </div>
                  ) : (
                    'Entrar'
                  )}
                </Button>

                {loginError && <Alert variant="danger">{loginError}</Alert>}
              </Form>
            )}
          </Formik>
        </Card.Body>
      </Card>
    </LoginContainer>
  );
};

export default Login;
